/**
 * Authenticated bot-invite state for the host shell. The add-bot control sits
 * next to the active-guild selector, so the browser session owns this small
 * API boundary rather than a planner-only surface.
 */
import { ref } from 'vue'
import type { Ref } from 'vue'

/** Payload returned by `GET /bot/invite`. */
export interface BotInviteResponse {
  readonly url: string
  readonly scopes: ReadonlyArray<string>
  readonly permissions: string
}

const API_BASE = import.meta.env.DEV
  ? ''
  : import.meta.env['VITE_API_BASE_URL'] ?? ''

const inviteUrl = ref<string | null>(null)
const loading = ref<boolean>(false)
const error = ref<string | null>(null)

/**
 * Resolves the Discord OAuth invite once for the active browser session.
 * Callers only invoke this after Discord authentication has completed.
 */
export function useBotInvite(): {
  readonly inviteUrl: Ref<string | null>
  readonly loading: Ref<boolean>
  readonly error: Ref<string | null>
  readonly fetch: () => Promise<void>
} {
  async function fetchInvite(): Promise<void> {
    if (loading.value || inviteUrl.value !== null) return

    loading.value = true
    error.value = null
    try {
      const res = await fetch(`${API_BASE}/bot/invite`, { credentials: 'include' })
      if (!res.ok) {
        error.value = `Failed to fetch bot invite URL (HTTP ${res.status})`
        return
      }

      const body = (await res.json()) as BotInviteResponse
      if (
        typeof body.url !== 'string'
        || body.url === ''
        || !body.url.startsWith('https://discord.com/')
      ) {
        error.value = 'Invalid invite URL returned by the api'
        return
      }

      inviteUrl.value = body.url
    } catch (cause: unknown) {
      error.value = cause instanceof Error ? cause.message : 'Failed to fetch bot invite URL'
    } finally {
      loading.value = false
    }
  }

  return {
    inviteUrl,
    loading,
    error,
    fetch: fetchInvite,
  }
}
